"use client";

import { Container, ContainerProps } from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect } from "react";
import { useSiteSettings } from "src/lib/hooks";
import { useLoginInfo } from "src/queries/emil/account";

type EmilEmbedProps = ContainerProps & {
	requiresAuth?: boolean;
	productCode: string;
	productName: string;
	scriptSrc: string;
	bookingFunnelCode?: string;
	productVersionId?: number;
	publicKey?: string;
};

export const EmilEmbed: React.FC<EmilEmbedProps> = ({
	productCode,
	productName,
	requiresAuth,
	scriptSrc,
	bookingFunnelCode,
	productVersionId,
	publicKey,
	...props
}) => {
	const router = useRouter();
	const loginInfo = useLoginInfo();
	const isVisible = !requiresAuth || Boolean(loginInfo);
	const { locale } = useSiteSettings();

	const init = useCallback(() => {
		if (typeof window.EMIL === "undefined") {
			return;
		}
		const api = window.EMIL(
			String(publicKey ?? process.env.EMIL_PUBLIC_KEY),
			{
				language: locale,
			}
		);

		if (!requiresAuth) {
			api.funnel(
				productCode,
				productName,
				"booking-funnel",
				bookingFunnelCode
					? {
							bookingFunnelCode,
							productVersionId,
						}
					: undefined,
			);

			return;
		}

		const unAuthorizedCallback = async () => {
			if (process.env.FEX_DEBUG_MODE)
				// eslint-disable-next-line no-alert
				alert(
					"For some reason you are not authorized to use this funnel. Check your console for the user object I just tried to use to authentiacte you.",
				);
			router.push("/");
		};

		if (!loginInfo) {
			return;
		}

		const userData = {
			userName: loginInfo.email,
			userFullName: loginInfo.name,
			tenantId: loginInfo.tenantId,
			tenantSlug: loginInfo.tenantSlug,
			sub: loginInfo.sub,
		};

		// eslint-disable-next-line no-console
		if (process.env.FEX_DEBUG_MODE) console.log(userData);

		api.funnel(
			productCode,
			productName,
			"booking-funnel",
			unAuthorizedCallback,
			userData,
		);
	}, [
		bookingFunnelCode,
		locale,
		loginInfo,
		productCode,
		productName,
		productVersionId,
		requiresAuth,
		router,
		publicKey,
	]);

	useEffect(() => {
		const script = document.createElement("script");

		script.id = "emil-init";
		script.async = true;
		script.src = scriptSrc;
		script.onload = init;

		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, [init, scriptSrc]);

	return isVisible ? (
		<Container maxW="container.xl" marginY={16} {...props}>
			{/* NOTE: the id has to be exactly this value for the time being, the injected styles are scoped to this selector */}
			<div data-axe-ignore data-testid="emil-embed" id="booking-funnel" />
		</Container>
	) : null;
};

// 🔬 TBD: Please evaluate
